/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import useCurrentUser from '../../customHook/useCurrentUser';
import socket from '../../features/socket';
import {
  getOrCreateAgentSessionId,
  loadAgentSessionDocument,
  saveAgentSessionDocument,
  clearAgentSessionDocument,
  addAgentSessionDocumentId,
  getAgentSessionDocumentIds,
  removeAgentSessionDocumentId,
} from '../../utils/session';

import { useUserHolidays } from '../../customHook/useUserHolidays';
import TypingIndicator from '../../components/AIFeature/TypingIndicator';
import AIReport from '../../components/AIFeature/AIReport';
import AISlip from '../../components/AIFeature/AiSlip';
import AiOfferLetter from '../../components/AIFeature/AiOfferLetter';
import ExperienceLetter from '../../components/AIFeature/AiExprerinceLetter';
import AiRelievingLetter from '../../components/AIFeature/AiRelievingLetter';
import DocumentPreview from '../../components/AIFeature/DocumentPreview';
import SuggestionList from '../../components/AIFeature/SuggestionList';
import useSidebarFilterStore from '../../store/useSidebarFilterStore';
import { Agent, ChatMessage } from '../../types/ai';
import { AGENTS } from '../../constants/constants';
import { useTaraSearch } from '../../layouts/TaraSearchContext';
import { useChatUploadMutation } from '../../features/apis/aiApi';
import { IoAttach } from 'react-icons/io5';
import { FaArrowUp } from 'react-icons/fa6';

const TARA_MESSAGES_KEY_PREFIX = 'tara_messages_';

const getTaraMessagesStorageKey = (sessionId: string) =>
  `${TARA_MESSAGES_KEY_PREFIX}${sessionId}`;

const TaraAI = () => {
  const user = useCurrentUser();
  const { holidays } = useUserHolidays();
  const { searchTerm, isSearchActive } = useTaraSearch();
  const [selectedAgent, setSelectedAgent] = useState<Agent>(() => {
    const currentActiveAgent = useSidebarFilterStore.getState().activeAgent;
    const agent = AGENTS.find(
      (a) => a.key === currentActiveAgent && !a.disabled
    );
    return agent || AGENTS[0];
  });

  const sessionId = useMemo(
    () => getOrCreateAgentSessionId(selectedAgent.key),
    [selectedAgent.key]
  );

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const sid = getOrCreateAgentSessionId(selectedAgent.key);
    try {
      const saved = localStorage.getItem(getTaraMessagesStorageKey(sid));
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map((msg: any) => {
          let component: React.ReactNode = null;
          const {
            rows,
            isTable,
            isExcel,
            componentFlag,
            OfferLetterData,
            ExperienceLetterData,
            PayslipData,
            RelievingLetterData,
            isDoc,
          } = msg;

          if (componentFlag === 'relieving_letter' && RelievingLetterData) {
            component = (
              <AiRelievingLetter details={RelievingLetterData} isDoc={isDoc} />
            );
          } else if (componentFlag === 'offer_letter' && OfferLetterData) {
            component = (
              <AiOfferLetter details={OfferLetterData} isDoc={isDoc} />
            );
          } else if (
            componentFlag === 'experience_letter' &&
            ExperienceLetterData
          ) {
            component = (
              <ExperienceLetter details={ExperienceLetterData} isDoc={isDoc} />
            );
          } else if (componentFlag === 'payslip' && PayslipData) {
            component = <AISlip payslipData={PayslipData} isDoc={isDoc} />;
          } else if (rows?.length > 0) {
            component = (
              <AIReport rows={rows} isTable={isTable} isExcel={isExcel} />
            );
          }

          return { ...msg, component };
        });
      }
    } catch (e) {
      console.error('Failed to load Tara messages from localStorage', e);
    }
    return [];
  });

  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);

  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [isCompanyRoomJoined, setIsCompanyRoomJoined] = useState(false);

  const chatBoxRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const responseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_HEIGHT = 150;
  const currentRequestRef = useRef<{
    eventName: string;
    statusEvent: string;
  } | null>(null);

  const [showAgentMenu, setShowAgentMenu] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [chatUpload] = useChatUploadMutation();
  const [uploadedDocument, setUploadedDocument] = useState<{
    documentId: string;
    fileName: string;
    fileType: string;
  } | null>(() => loadAgentSessionDocument(selectedAgent.key));

  const [isUploadingDocument, setIsUploadingDocument] = useState(false);

  // Persist uploaded document to the current agent session
  useEffect(() => {
    saveAgentSessionDocument(selectedAgent.key, uploadedDocument);
  }, [uploadedDocument, selectedAgent.key]);

  // Restore uploaded document when switching agents
  useEffect(() => {
    const saved = loadAgentSessionDocument(selectedAgent.key);
    setUploadedDocument(saved);
  }, [selectedAgent.key]);

  // Socket connection and room joining
  useEffect(() => {
    if (!user?.companyId) return;

    const handleConnect = () => {
      console.log('✅ Socket connected');
      setIsSocketConnected(true);

      // Join company room on connect
      if (user.companyId) {
        console.log(`📡 Joining company room: company-${user.companyId}`);
        socket.emit('joinCompany', user.companyId);
      }
    };

    const handleDisconnect = () => {
      console.log('❌ Socket disconnected');
      setIsSocketConnected(false);
      setIsCompanyRoomJoined(false);
    };

    const handleJoinedCompany = (data: any) => {
      console.log('✅ Successfully joined company room:', data);
      setIsCompanyRoomJoined(true);
    };

    const handleReconnect = () => {
      console.log('🔄 Socket reconnected');
      setIsSocketConnected(true);
      if (user.companyId) {
        socket.emit('joinCompany', user.companyId);
      }
    };

    // Set up listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('reconnect', handleReconnect);
    socket.on('joinedCompany', handleJoinedCompany);

    // If socket is already connected, join room immediately
    if (socket.connected) {
      setIsSocketConnected(true);
      socket.emit('joinCompany', user.companyId);
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('reconnect', handleReconnect);
      socket.off('joinedCompany', handleJoinedCompany);

      // Leave room on unmount
      if (user.companyId) {
        socket.emit('leaveCompany', user.companyId);
      }
    };
  }, [user?.companyId]);

  // Handle offline messages
  useEffect(() => {
    let offlineMessageShown = false;

    const handleDisconnect = () => {
      setIsTyping(false);
      setStatusLabel(null);
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;
        if (!offlineMessageShown) {
          offlineMessageShown = true;
          setMessages((prev) => [
            ...prev,
            {
              direction: 'incoming',
              message: 'Tara seems to be offline right now. Please try again.',
              tags: ['🔁 Try Again'],
            },
          ]);
        }
      }
    };

    const handleReconnect = () => {
      offlineMessageShown = false;
    };

    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleDisconnect);
    socket.on('reconnect', handleReconnect);
    socket.on('connect', handleReconnect);

    return () => {
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleDisconnect);
      socket.off('reconnect', handleReconnect);
      socket.off('connect', handleReconnect);
    };
  }, []);

  // Update sidebar filter
  useEffect(() => {
    const agentContext =
      selectedAgent.key === 'hr'
        ? 'hr'
        : selectedAgent.key === 'recruitment'
        ? 'recruitment'
        : selectedAgent.key === 'project'
        ? 'project'
        : null;
    useSidebarFilterStore.getState().setActiveAgent(agentContext);
  }, [selectedAgent.key]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatBoxRef.current) {
      const el = chatBoxRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, isTyping]);

  // Load messages for the currently selected agent
  useEffect(() => {
    const sid = getOrCreateAgentSessionId(selectedAgent.key);
    try {
      const saved = localStorage.getItem(getTaraMessagesStorageKey(sid));
      if (saved) {
        const parsed = JSON.parse(saved);
        const hydrated = parsed.map((msg: any) => {
          let component: React.ReactNode = null;
          const {
            rows,
            isTable,
            isExcel,
            componentFlag,
            OfferLetterData,
            ExperienceLetterData,
            PayslipData,
            RelievingLetterData,
            isDoc,
          } = msg;

          if (componentFlag === 'relieving_letter' && RelievingLetterData) {
            component = (
              <AiRelievingLetter details={RelievingLetterData} isDoc={isDoc} />
            );
          } else if (componentFlag === 'offer_letter' && OfferLetterData) {
            component = (
              <AiOfferLetter details={OfferLetterData} isDoc={isDoc} />
            );
          } else if (
            componentFlag === 'experience_letter' &&
            ExperienceLetterData
          ) {
            component = (
              <ExperienceLetter details={ExperienceLetterData} isDoc={isDoc} />
            );
          } else if (componentFlag === 'payslip' && PayslipData) {
            component = <AISlip payslipData={PayslipData} isDoc={isDoc} />;
          } else if (rows?.length > 0) {
            component = (
              <AIReport rows={rows} isTable={isTable} isExcel={isExcel} />
            );
          }

          return { ...msg, component };
        });
        setMessages(hydrated);
      } else {
        setMessages([]);
      }
    } catch (e) {
      console.error('Failed to load Tara messages from localStorage', e);
      setMessages([]);
    }
  }, [selectedAgent.key]);

  // Persist TaraAI messages scoped to the current agent session
  useEffect(() => {
    const sid = getOrCreateAgentSessionId(selectedAgent.key);
    try {
      const toSave = messages.map(({ component, componentFlag, ...rest }) => ({
        ...rest,
        componentFlag,
      }));
      localStorage.setItem(
        getTaraMessagesStorageKey(sid),
        JSON.stringify(toSave)
      );
    } catch (e) {
      console.error('Failed to save Tara messages to localStorage:', e);
    }
  }, [messages, selectedAgent.key]);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    const prevHeight = el.style.height;
    el.style.height = 'auto';
    const newHeight = Math.min(el.scrollHeight, MAX_HEIGHT);
    const finalHeight = newHeight + 'px';

    if (prevHeight !== finalHeight) {
      el.style.height = finalHeight;
      el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
    } else {
      el.style.height = prevHeight;
    }
  }, [userInput]);

  // Click outside agent menu
  useEffect(() => {
    if (!showAgentMenu) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        menuContainerRef.current &&
        !menuContainerRef.current.contains(event.target as Node)
      ) {
        setShowAgentMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showAgentMenu]);
  useEffect(() => {
    if (showAgentMenu && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [showAgentMenu]);

  useEffect(() => {
    return () => {
      if (currentRequestRef.current) {
        socket.off(currentRequestRef.current.statusEvent);
      }
      if (responseTimeoutRef.current) {
        clearTimeout(responseTimeoutRef.current);
      }
    };
  }, []);

  const handleSend = useCallback(
    (text: string) => {
      if (!text.trim() && !uploadedDocument) return;
      if (isUploadingDocument) return;
      if (!isSocketConnected) {
        setMessages((prev) => [
          ...prev,
          {
            direction: 'incoming',
            message:
              'You are offline. Please check your connection and try again.',
            tags: ['🔄 Retry'],
          },
        ]);
        return;
      }

      if (!isCompanyRoomJoined) {
        console.warn('⚠️ Not in company room, attempting to join...');
        if (user?.companyId) {
          socket.emit('joinCompany', user.companyId);
        }
      }

      const isRecruiter = selectedAgent.key === 'recruitment';
      const isProject = selectedAgent.key === 'project';
      const prefix = isProject
        ? 'ai:project'
        : isRecruiter
        ? 'ai:recruiter'
        : 'ai:hr';
      const eventName = `${prefix}:dashboard`;
      const errorEvent = `${prefix}:error`;
      const resEvent = `${prefix}:response`;
      const statusEvent = `${prefix}:status`;
      const pendingDocument = uploadedDocument;
      const sessionDocumentIds = getAgentSessionDocumentIds(selectedAgent.key);
      setMessages((prev) => [
        ...prev,
        {
          direction: 'outgoing',
          message:
            text ||
            (pendingDocument ? 'Please review the attached document.' : ''),
          document: pendingDocument
            ? {
                documentId: pendingDocument.documentId,
                fileName: pendingDocument.fileName,
                fileType: pendingDocument.fileType,
              }
            : undefined,
        },
      ]);

      setUserInput('');
      setUploadedDocument(null);
      setIsTyping(true);
      setStatusLabel('Thinking...');

      const payload = {
        message: `${text} ${
          pendingDocument
            ? ` consider checking (${pendingDocument.fileName})`
            : ''
        }`,
        companyId: user?.companyId,
        employeeId: user?._id,
        sessionId,
        holidays,
        agent: selectedAgent.key,
        ...(pendingDocument ? { documentId: pendingDocument.documentId } : {}),
        ...(sessionDocumentIds.length > 0
          ? {
              documentIds: sessionDocumentIds.map((id) => ({
                id,
                lastUploaded: pendingDocument
                  ? id === pendingDocument.documentId
                  : false,
                note:
                  pendingDocument && id === pendingDocument.documentId
                    ? 'This was last uploaded and may be used for comments'
                    : 'Previously uploaded in this session',
              })),
            }
          : {}),
      };

      let hasErrorBeenHandled = false;

      socket.off(statusEvent);
      socket.off(resEvent);
      socket.off(errorEvent);

      // Store current request info for cleanup
      currentRequestRef.current = { eventName, statusEvent };

      // Set up status listener (use .on for multiple status updates)
      socket.on(statusEvent, (payload: { stage?: string; label?: string }) => {
        console.log('📡 STATUS RECEIVED:', payload);

        if (payload?.label) {
          setStatusLabel(payload.label);
        }
        if (payload?.stage) {
          console.log('📡 Stage:', payload.stage);
        }
      });

      // Set up response listener (use .once for single response)
      socket.once(resEvent, (response) => {
        console.log('📩 RESPONSE RECEIVED');

        // Clear timeout
        if (responseTimeoutRef.current) {
          clearTimeout(responseTimeoutRef.current);
          responseTimeoutRef.current = null;
        }

        // Clean up status listener
        socket.off(statusEvent);
        currentRequestRef.current = null;

        // Backend sends { response: extracted }
        const actualResponse = response.response ?? response;

        const {
          message,
          tags,
          rows,
          isTable,
          isExcel,
          component: componentFlag,
          OfferLetterData,
          ExperienceLetterData,
          PayslipData,
          RelievingLetterData,
          isDoc,
        } = actualResponse;

        const finalMessage = message || 'Response generated successfully.';
        let finalComponent: React.ReactNode = null;
        let finalTags: string[] = tags || [];

        if (componentFlag === 'relieving_letter' && RelievingLetterData) {
          finalComponent = (
            <AiRelievingLetter details={RelievingLetterData} isDoc={isDoc} />
          );
          finalTags = tags || ['View Milestones', 'Back to Project'];
        } else if (componentFlag === 'offer_letter' && OfferLetterData) {
          finalComponent = (
            <AiOfferLetter details={OfferLetterData} isDoc={isDoc} />
          );
          finalTags = tags || ['View Milestones', 'Back to Project'];
        } else if (
          componentFlag === 'experience_letter' &&
          ExperienceLetterData
        ) {
          finalComponent = (
            <ExperienceLetter details={ExperienceLetterData} isDoc={isDoc} />
          );
          finalTags = tags || ['View Milestones', 'Back to Project'];
        } else if (componentFlag === 'payslip' && PayslipData) {
          finalComponent = <AISlip payslipData={PayslipData} isDoc={isDoc} />;
          finalTags = tags || ['View Payslip', 'Back to Profile'];
        } else if (rows?.length > 0) {
          finalComponent = (
            <AIReport rows={rows} isTable={isTable} isExcel={isExcel} />
          );
        }

        setMessages((prev) => [
          ...prev,
          {
            direction: 'incoming',
            message: finalMessage,
            tags: finalTags,
            rows,
            isTable,
            isExcel,
            component: finalComponent,
            componentFlag,
            OfferLetterData,
            ExperienceLetterData,
            PayslipData,
            RelievingLetterData,
          },
        ]);

        setIsTyping(false);
        setStatusLabel(null);
      });

      socket.once(errorEvent, (err) => {
        console.log('❌ ERROR RECEIVED:', err);

        // Clear timeout
        if (responseTimeoutRef.current) {
          clearTimeout(responseTimeoutRef.current);
          responseTimeoutRef.current = null;
        }

        // Clean up status listener
        socket.off(statusEvent);
        currentRequestRef.current = null;

        if (hasErrorBeenHandled) return;
        hasErrorBeenHandled = true;

        let userFriendlyMessage = 'Something went wrong. Please try again.';
        const rawMsg = err?.message?.toLowerCase?.() || '';

        if (
          rawMsg.includes('rate limit') ||
          rawMsg.includes('free-models-per-day') ||
          rawMsg.includes('429')
        ) {
          userFriendlyMessage =
            'You have reached the daily AI limit. Please try again tomorrow or upgrade the plan.';
        } else if (rawMsg.includes('overload') || rawMsg.includes('503')) {
          userFriendlyMessage =
            'Our AI assistant is currently overloaded. Please wait a few seconds and try again.';
        } else if (rawMsg.includes('timeout')) {
          userFriendlyMessage =
            'The request timed out. Please try again shortly.';
        } else if (rawMsg.includes('network')) {
          userFriendlyMessage =
            "We're having trouble connecting. Please check your internet and try again.";
        } else if (
          rawMsg.includes('google') ||
          rawMsg.includes('generativeai')
        ) {
          userFriendlyMessage =
            "There's a temporary issue with our AI model. Please retry in a moment.";
        }

        setMessages((prev) => [
          ...prev,
          {
            direction: 'incoming',
            message: userFriendlyMessage,
            tags: ['🔁 Retry', '🔙 Back'],
          },
        ]);

        setIsTyping(false);
        setStatusLabel(null);
      });

      // Set up timeout
      const responseTimeout = setTimeout(() => {
        responseTimeoutRef.current = null;
        if (!hasErrorBeenHandled) {
          console.warn('⏰ Request timed out');
          setIsTyping(false);
          setStatusLabel(null);
          socket.off(statusEvent);
          currentRequestRef.current = null;

          setMessages((prev) => [
            ...prev,
            {
              direction: 'incoming',
              message: 'The request timed out. Please try again shortly.',
              tags: ['🔁 Retry', '🔙 Back'],
            },
          ]);
        }
      }, 180000);

      responseTimeoutRef.current = responseTimeout;

      // Emit the message
      console.log('📤 Emitting:', eventName, { payload, socketId: socket.id });
      socket.emit(eventName, payload);
    },
    [
      user,
      sessionId,
      holidays,
      selectedAgent,
      isSocketConnected,
      isCompanyRoomJoined,
      uploadedDocument,
      isUploadingDocument,
    ]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isTyping && !isUploadingDocument) handleSend(userInput);
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    if (!allowedTypes.includes(file.type)) {
      setMessages((prev) => [
        ...prev,
        {
          direction: 'incoming',
          message:
            'Invalid file type. Please upload a PDF, CSV, Excel, or Word document.',
          tags: ['🔁 Retry'],
        },
      ]);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setMessages((prev) => [
        ...prev,
        {
          direction: 'incoming',
          message: 'File size exceeds 10MB limit.',
          tags: ['🔁 Retry'],
        },
      ]);
      return;
    }
    setUploadedDocument(null);
    setIsUploadingDocument(true);

    const formData = new FormData();
    formData.append('file', file);
    if (user?.companyId) {
      formData.append('companyId', user.companyId);
    }
    if (sessionId) {
      formData.append('sessionId', sessionId);
    }
    const agentType =
      selectedAgent.key === 'recruitment'
        ? 'recruiter'
        : selectedAgent.key === 'project'
        ? 'project'
        : selectedAgent.key === 'hr'
        ? 'hr'
        : selectedAgent.key;
    formData.append('agentType', agentType);
    const tag =
      selectedAgent.key === 'recruitment'
        ? 'Recruitment'
        : selectedAgent.key === 'project'
        ? 'Project'
        : selectedAgent.key === 'hr'
        ? 'HR'
        : selectedAgent.key;
    formData.append('tag', tag);

    try {
      const response = await chatUpload({ data: formData }).unwrap();
      const uploaded = {
        documentId: response.documentId,
        fileName: response.fileName,
        fileType: file.type,
      };
      setUploadedDocument(uploaded);
      addAgentSessionDocumentId(selectedAgent.key, uploaded.documentId);
    } catch (error: any) {
      const message =
        error?.data?.message || 'Document upload failed. Please try again.';
      setMessages((prev) => [
        ...prev,
        {
          direction: 'incoming',
          message,
          tags: ['🔁 Retry'],
        },
      ]);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const visibleMessages = useMemo(() => {
    if (!isSearchActive || !searchTerm.trim()) return messages;
    const term = searchTerm.toLowerCase();
    return messages.filter((m) => m.message?.toLowerCase().includes(term));
  }, [messages, searchTerm, isSearchActive]);

  const isEmptyState = messages.length <= 0;

  const renderInputBar = () => {
    const handleBarClick = () => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    };

    return (
      <div className="relative p-[1.5px] rounded-2xl overflow-visible animate-spin-border">
        {' '}
        {isInputFocused &&
          userInput.trim().length > 0 &&
          !uploadedDocument &&
          !isUploadingDocument &&
          isSocketConnected && (
            <SuggestionList
              suggestions={selectedAgent.suggestions}
              userInput={userInput}
              onSelect={(suggestion) => {
                handleSend(suggestion.label);
              }}
              onClose={() => setIsInputFocused(false)}
            />
          )}
        <div
          className="bg-white px-4 pt-2 pb-2 rounded-2xl overflow-visible"
          onClick={handleBarClick}
        >
          {(uploadedDocument || isUploadingDocument) && (
            <div className="mb-2" onClick={(e) => e.stopPropagation()}>
              <DocumentPreview
                fileName={uploadedDocument?.fileName || '...'}
                fileType={uploadedDocument?.fileType || ''}
                isUploading={isUploadingDocument}
                onRemove={
                  uploadedDocument
                    ? () => {
                        const doc = uploadedDocument;
                        setUploadedDocument(null);
                        clearAgentSessionDocument(selectedAgent.key);
                        removeAgentSessionDocumentId(
                          selectedAgent.key,
                          doc.documentId
                        );
                      }
                    : undefined
                }
              />
            </div>
          )}
          <div className="flex items-center gap-2 py-5">
            <textarea
              ref={textareaRef}
              className="flex-1 min-w-0 resize-none bg-white focus:outline-none focus:ring-0 border-none overflow-hidden hide-scrollbar"
              placeholder={
                isUploadingDocument
                  ? 'Ingesting document...'
                  : isTyping
                  ? statusLabel || 'Tara is thinking...'
                  : !isSocketConnected
                  ? 'Connecting...'
                  : uploadedDocument
                  ? `Ask about ${uploadedDocument.fileName}...`
                  : selectedAgent.placeholder
              }
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (!isUploadingDocument && isSocketConnected) {
                  setIsInputFocused(true);
                }
              }}
              onBlur={() => setIsInputFocused(false)}
              disabled={isTyping || isUploadingDocument || !isSocketConnected}
              rows={1}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.csv,.xlsx,.xls,.docx,.doc"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
            />
          </div>
          <div
            ref={menuContainerRef}
            className="flex items-center gap-1 mt-1 relative"
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isTyping || isUploadingDocument}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-40 transition-colors"
            >
              <IoAttach size={16} />
            </button>
            <button
              type="button"
              className="text-gray-400 text-xl leading-none px-1"
              onClick={() => setShowAgentMenu((prev) => !prev)}
            >
              +
            </button>
            <button
              type="button"
              onClick={() => setShowAgentMenu((prev) => !prev)}
              className="flex items-center gap-2 bg-gradient-to-tr from-[#6E3EF5] to-[#A818D8] text-white text-xs font-medium py-1 px-4 rounded-full"
            >
              {selectedAgent.label}
              {!isSocketConnected && ' (offline)'}
            </button>
            {(userInput.trim() || uploadedDocument) && (
              <button
                onClick={() => {
                  if (
                    !isTyping &&
                    !isUploadingDocument &&
                    (userInput.trim() || uploadedDocument) &&
                    isSocketConnected
                  ) {
                    handleSend(userInput.trim());
                  }
                }}
                disabled={
                  isTyping ||
                  isUploadingDocument ||
                  (!userInput.trim() && !uploadedDocument) ||
                  !isSocketConnected
                }
                className="flex-shrink-0 text-gray-500 disabled:opacity-40 transition-opacity ml-auto bg-[#F4EFFF] rounded-full p-2 hover:bg-[#D6D1FF] hover:text-[#6E3EF5] cursor-pointer"
              >
                <FaArrowUp size={14} color="#7F35F1" />
              </button>
            )}

            {showAgentMenu && (
              <div className="absolute bottom-full left-0 mb-2 w-60 max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-md p-2 z-50">
                {AGENTS.map((agent) => (
                  <button
                    key={agent.key}
                    type="button"
                    onClick={() => {
                      if (!agent.disabled) {
                        setSelectedAgent(agent);
                        setShowAgentMenu(false);
                      }
                    }}
                    disabled={agent.disabled}
                    title={agent.disabled ? 'Coming soon' : undefined}
                    className={`flex items-center gap-2 w-full text-left px-2 py-2 rounded-lg text-sm ${
                      agent.disabled
                        ? 'opacity-50 cursor-not-allowed'
                        : agent.key === selectedAgent.key
                        ? 'bg-purple-50 text-purple-700'
                        : 'text-gray-700 hover:bg-purple-50'
                    }`}
                  >
                    <img
                      src={agent.icon}
                      alt={agent.label}
                      className="h-5 w-5"
                    />
                    <span className="flex flex-col leading-tight">
                      <span>{agent.label}</span>
                      {agent.disabled && (
                        <span className="text-[10px] text-gray-400 italic">
                          Coming soon
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="relative w-full bg-white font-manrope overflow-hidden flex flex-col px-4"
      style={{
        height: 'calc(100vh - 5rem)',
        background:
          'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 40%, #F3EEFF 65%, #D9CCFF 100%)',
      }}
    >
      <div className="relative flex flex-col h-full">
        {isEmptyState ? (
          <>
            <div className="flex-1 overflow-y-auto hide-scrollbar w-full flex items-center justify-center">
              <div className="w-full max-w-7xl mx-auto">
                <p className="text-center text-gray-500 text-lg mb-6">
                  Pick an assistant to get started, or just type your own
                  question below{' '}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {AGENTS.map((agent) => {
                    const isSelected = selectedAgent.key === agent.key;
                    const isDisabled = agent.disabled;
                    return (
                      <button
                        key={agent.key}
                        type="button"
                        onClick={() => {
                          if (!isDisabled) {
                            setSelectedAgent(agent);
                          }
                        }}
                        disabled={isDisabled}
                        title={isDisabled ? 'Coming soon' : undefined}
                        className={`flex items-start gap-3 text-left border rounded-xl p-4 bg-[#FAF9FF] transition ${
                          isDisabled
                            ? 'opacity-50 cursor-not-allowed'
                            : isSelected
                            ? 'bg-purple-50 shadow-sm'
                            : 'border-gray-100 opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={agent.icon}
                          alt={agent.label}
                          className="h-20 w-20 rounded-lg object-cover flex-shrink-0"
                        />
                        <div>
                          <p className="text-[#6E3EF5] font-semibold text-sm">
                            {agent.label}
                          </p>
                          <p className="text-[#7D7D7D] text-xs mt-1">
                            {agent.collapsedDescription}
                          </p>
                          {isDisabled && (
                            <p className="text-[10px] text-gray-400 mt-1 italic">
                              Coming soon
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="w-full">
              <div className="max-w-7xl mx-auto bg-gradient-to-r from-[#FFFFFF] to-[#F4F2FF] rounded-t-3xl pt-8 relative overflow-visible">
                <div className="relative text-center px-4">
                  <span className="font-bold text-3xl"> Welcome to </span>
                  <span className="font-bold text-3xl text-[#991AC7]">
                    Tara
                  </span>
                  <p className="text-[#505050] mt-2 text-xs md:text-sm">
                    <span className="text-[#505050] font-semibold">
                      {selectedAgent.descriptionTitle}
                    </span>{' '}
                    - {selectedAgent.descriptionBody}
                  </p>
                </div>

                <div className="w-full max-w-5xl mx-auto px-4 relative mt-6">
                  {renderInputBar()}
                </div>

                <p className="mt-4 text-sm md:text-base text-gray-500 font-inter w-full text-center pb-2">
                  Simplify operations,{' '}
                  <span className="text-[#6E3EF5]">Share updates, </span>
                  <span> Manage delegation.</span>.
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              ref={chatBoxRef}
              className="flex-1 overflow-y-auto hide-scrollbar w-full"
            >
              <div className="w-full max-w-5xl mx-auto px-4 py-6">
                {visibleMessages.length === 0 ? (
                  <div className="h-full flex items-center justify-center min-h-[30vh] text-gray-400 text-sm">
                    No messages match &quot;{searchTerm}&quot;
                  </div>
                ) : (
                  <div className="w-full space-y-4">
                    {visibleMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex w-full ${
                          msg.direction === 'outgoing'
                            ? 'justify-end'
                            : 'justify-start'
                        }`}
                      >
                        <div
                          className={`p-4 max-w-full rounded-2xl shadow-sm ${
                            msg.direction === 'outgoing'
                              ? 'bg-gray-200 text-right font-inter'
                              : 'bg-white text-left font-inter border border-gray-200'
                          } ${msg.component ? 'w-full' : ''}`}
                          style={{ whiteSpace: 'pre-line' }}
                        >
                          {msg.message && <p>{msg.message}</p>}

                          {msg.document && (
                            <div
                              className="mt-2"
                              style={{ whiteSpace: 'normal' }}
                            >
                              <DocumentPreview
                                fileName={msg.document.fileName}
                                fileType={msg.document.fileType}
                                isUploaded
                              />
                            </div>
                          )}

                          {msg.component && (
                            <div
                              className="mt-3"
                              style={{ whiteSpace: 'normal' }}
                            >
                              {msg.component}
                            </div>
                          )}

                          {msg.tags && msg.tags.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {msg.tags.map((tag, index) => (
                                <button
                                  key={index}
                                  onClick={() => {
                                    if (!isTyping) handleSend(tag);
                                  }}
                                  disabled={isTyping}
                                  className="py-1 px-3 rounded-full text-sm transition text-purple-700 hover:bg-purple-200"
                                >
                                  {tag}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {isTyping && (
                      <TypingIndicator label={statusLabel || 'Thinking...'} />
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full">
              <div className="w-full max-w-5xl mx-auto px-4 relative">
                {renderInputBar()}
              </div>

              <p className="mt-4 text-sm md:text-base text-gray-500 font-inter w-full text-center pb-2">
                Simplify operations,{' '}
                <span className="text-[#6E3EF5]">Share updates, </span>
                <span> Manage delegation.</span>.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TaraAI;
